"use client";
import { motion, Variants } from "framer-motion";
import SectionTitle from "@/shared/app/ui/text/PageSectionTitle";
import { useEffect, useState } from "react";
import Image from "next/image";
const steps = [
  {
    num: "1",
    title: "대상자 모집",
    desc: "데이터 구축을 위한 참여자를 모집합니다.",
  },
  {
    num: "2",
    title: "데이터 수집",
    desc: "실제 환경에서 다양한 형태의 데이터를 수집합니다.",
  },
  {
    num: "3",
    title: "데이터 전처리",
    desc: "불필요한 요소를 제거하고 적합하게 정리합니다.",
  },
  {
    num: "4",
    title: "데이터 가공",
    desc: "AI 학습에 최적화될 수 있도록 가공합니다.",
  },
  {
    num: "5",
    title: "데이터 검수",
    desc: "가공된 데이터의 품질을 확인하고 보완합니다.",
  },
];

const container: Variants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      ease: "easeOut",
      duration: 0.6,
    },
  },
};

const itemLeft: Variants = {
  hidden: { opacity: 0, x: -40 },
  show: { opacity: 1, x: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const itemRight: Variants = {
  hidden: { opacity: 0, x: 40 },
  show: { opacity: 1, x: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const iconMotion: Variants = {
  hidden: { opacity: 0, y: -20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
};
export default function AiDataSection() {
  return (
    <div className="border-t border-[#ECECEC] mt-[-2px]">
      <section
        className={`w-full relative bg-center overflow-hidden py-20 md:py-60 transition-opacity duration-700  px-5 xl:px-2`}
        style={{
          backgroundImage:
            "url('/assets/images/page/service/aidata/background.jpg')",
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-[#8E90FF]/30 via-[#9194D0]/40 to-[#B5B7FF]/25 backdrop-blur-[4.5px] mix-blend-soft-light" />

        <div
          className="relative flex flex-col w-full xl:max-w-[1280px] mx-auto z-10 rounded-3xl"
          style={{ backgroundColor: "rgba(253,253,253,0.8)" }}
        >
          <div className="md:py-24 md:px-14 py-10 px-6">
            <SectionTitle
              align="left"
              label="AI Data"
              title={<>장애인 특화 AI 학습 데이터</>}
              description={
                <>
                  풍부한 인적 인프라를 바탕으로 대상자 모집과 고품질 AI 학습
                  데이터 제공이 가능하며,
                  <br />
                  나아가 AI 제품 실증 과정까지 함께 지원합니다.
                </>
              }
              colorMode="light"
            />

            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.3 }}
              variants={{
                hidden: { opacity: 0, y: 60 },
                show: {
                  opacity: 1,
                  y: 0,
                  transition: {
                    duration: 0.9,
                    ease: [0.25, 0.1, 0.25, 1],
                    staggerChildren: 0.1,
                  },
                },
              }}
              className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 gap-y-3  md:gap-6 md:mt-24 mt-20"
            >
              {steps.map((step) => (
                <motion.div
                  key={step.num}
                  variants={{
                    hidden: { opacity: 0, y: 40 },
                    show: {
                      opacity: 1,
                      y: 0,
                      transition: { duration: 0.6, ease: "easeOut" },
                    },
                  }}
                  className="bg-[#FCFCFC]/80 backdrop-blur-md rounded-3xl shadow-md py-6 px-5 md:py-8 md:px-7 hover:shadow-lg transition-all duration-300"
                >
                  <div className="flex items-center gap-2 mb-2 md:mb-3">
                    <span
                      className="md:w-7 md:h-7 w-5 h-5 flex items-center justify-center rounded-full bg-[#0075FF] text-white text-sm"
                      style={{ fontFamily: '"pre-Medium", sans-serif' }}
                    >
                      {step.num}
                    </span>
                    <p
                      className="text-base md:text-lg font-semibold text-black-almostBlack tracking-tight"
                      style={{ fontFamily: '"pre-SemiBold", sans-serif' }}
                    >
                      {step.title}
                    </p>
                  </div>
                  <p
                    className="text-[15px] md:text-base text-midGray-30 leading-5 tracking-tight"
                    style={{ fontFamily: '"pre-Medium", sans-serif' }}
                  >
                    {step.desc}
                  </p>
                </motion.div>
              ))}
            </motion.div>

            {/* 아이콘 */}
            <motion.div
              variants={iconMotion}
              className="relative w-10 h-7 md:w-14 md:h-10 overflow-hidden m-auto my-10 md:my-20"
            >
              <Image
                src="/assets/images/page/service/aidata/arrow.svg"
                alt="arrowIcon"
                fill
                className="object-contain"
              />
            </motion.div>
            {/* 컨테이너 */}
            <motion.div
              variants={container}
              className="bg-white flex space-x-10 md:py-5 md:px-5 px-3 py-3 gap-3 md:gap-10 text-white lg:flex-row flex-col rounded-4xl"
            >
              {/* Left Item */}
              <motion.div
                variants={itemLeft}
                className="bg-[#4F9BFF] flex flex-1 lg:w-auto w-full flex-col text-center m-auto md:px-4 md:py-6 py-4 px-3 rounded-3xl shadow-md"
              >
                <span
                  className="bg-[#3983E5] self-center px-4 py-2 rounded-lg tracking-tight leading-5 md:text-base text-sm"
                  style={{ fontFamily: '"pre-Medium", sans-serif' }}
                >
                  Disabled Infrastructure
                </span>
                <p
                  className="text-base md:text-xl tracking-tight mt-2"
                  style={{ fontFamily: '"pre-SemiBold", sans-serif' }}
                >
                  장애인 인프라
                </p>
              </motion.div>

              {/* Right Item */}
              <motion.div
                variants={itemRight}
                className="bg-[#4F9BFF] flex flex-1 lg:w-auto w-full flex-col text-center m-auto md:px-4 md:py-6 py-4 px-3 rounded-3xl shadow-md"
              >
                <span
                  className="bg-[#3983E5] self-center px-4 py-2 rounded-lg tracking-tight leading-5 md:text-base text-sm"
                  style={{ fontFamily: '"pre-Medium", sans-serif' }}
                >
                  AI demonstration participation
                </span>
                <p
                  className="text-base md:text-xl tracking-tight mt-2"
                  style={{ fontFamily: '"pre-SemiBold", sans-serif' }}
                >
                  AI 실증 참여
                </p>
              </motion.div>
            </motion.div>
            {/*  */}
          </div>
        </div>
      </section>
    </div>
  );
}
